import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Typography, Tabs, Tab, CircularProgress, Button, Container,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Pagination, TextField, Stack, Accordion, AccordionSummary, AccordionDetails, Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { Document, Page, pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const PAGE_SIZE = 10;

const EvaluationReport = ({ evaluation }) => {
  if (!evaluation) return null;

  let details = {};
  if (typeof evaluation.evaluation_details === 'object' && evaluation.evaluation_details !== null) {
    details = evaluation.evaluation_details;
  } else if (typeof evaluation.evaluation_details === 'string') {
    try {
      details = JSON.parse(evaluation.evaluation_details);
    } catch (e) {
      console.error("Failed to parse evaluation_details:", e);
    }
  }

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: '12px', border: '1px solid #e0e0e0', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Chip 
          icon={<StarIcon />} 
          label={`평가 점수: ${evaluation.score ?? 'N/A'} / 6`} 
          color="primary" 
          sx={{ fontSize: '1.1rem', fontWeight: 'bold', p: 2 }}
        />
        {evaluation.evaluated_at &&
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {`평가 완료: ${new Date(evaluation.evaluated_at).toLocaleDateString('ko-KR')}`}
          </Typography>
        }
      </Box>

      <Box>
        {Object.entries(details).map(([issue, data]) => (
          <Accordion key={issue} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 'bold' }}>{issue}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Chip 
                label={`점수: ${data.score || 'N/A'}`} 
                color="info"
                size="small"
                sx={{ alignSelf: 'flex-start' }}
              />
              <Typography variant="body2" component="div" dangerouslySetInnerHTML={{ __html: data.feedback }} />
            </AccordionDetails>
          </Accordion>
        ))}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <LightbulbIcon color="warning" sx={{ mr: 1 }} />
            <Typography sx={{ fontWeight: 'bold' }}>총평 및 조언</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography component="div" dangerouslySetInnerHTML={{ __html: evaluation.overall_comment }} />
          </AccordionDetails>
        </Accordion>
      </Box>
    </Paper>
  );
};

const MyReviewPage = () => {
  const [mySubmits, setMySubmits] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const { user, loading: authLoading } = useAuth();
  const [answerTab, setAnswerTab] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filteredSubmits, setFilteredSubmits] = useState([]);

  useEffect(() => {
    if (authLoading) return; 

    const fetchData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      setLoading(true);

      const { data: mee, error: meeError } = await supabase
        .from('mee_answers')
        .select('id, set_id, set_title, submission_time, exam_year, exam_month, subject, score, evaluation_status, submission_group_id, user_id')
        .eq('user_id', user.id)
        .order('submission_time', { ascending: false });

      const { data: mpt } = await supabase
        .from('mpt_answers')
        .select('id, set_id, set_title, submission_time, exam_year, exam_month, score, evaluation_status, user_id')
        .eq('user_id', user.id)
        .order('submission_time', { ascending: false });

      const meeList = (mee || []).map(row => ({ ...row, exam_type: 'mee' }));
      const mptList = (mpt || []).map(row => ({ ...row, exam_type: 'mpt' }));

      const all = [...meeList, ...mptList];
      const unique = Array.from(
        new Map(all.map(item => [item.exam_type + item.set_id, item])).values()
      );
      setMySubmits(unique.sort((a, b) => new Date(b.submission_time) - new Date(a.submission_time)));
      setLoading(false);
    };
    fetchData();
  }, [user, authLoading]);

  useEffect(() => {
    let filtered = mySubmits;
    if (search) {
      filtered = mySubmits.filter(item =>
        (item.set_title || item.subject || '').toLowerCase().includes(search.toLowerCase())
      );
    }
    setFilteredSubmits(filtered);
    setPage(1);
  }, [search, mySubmits]);

  const handleSelect = async (set_id, exam_type) => {
    setLoading(true);
    setAnswerTab(0);
    
    const setTable = exam_type === 'mee' ? 'mee_paper_sets' : 'mpt_paper_sets';
    const { data: setInfo } = await supabase
      .from(setTable)
      .select('*')
      .eq('id', set_id)
      .single();

    const answerTable = exam_type === 'mee' ? 'mee_answers' : 'mpt_answers';
    const { data: myAnswers } = await supabase
      .from(answerTable)
      .select('*')
      .eq('user_id', user.id)
      .eq('set_id', set_id)
      .order('submission_time', { ascending: false });

    const processedMyAnswers = myAnswers ? await Promise.all(myAnswers.map(async (answer) => {
      if (answer.evaluation_status === '평가 완료') {
        const evaluationTable = exam_type === 'mee' ? 'mee_evaluations' : 'mpt_evaluations';
        const answerIdColumn = 'answer_id';
        const { data: evaluationData, error } = await supabase
          .from(evaluationTable)
          .select('score, evaluation_details, overall_comment, evaluated_at')
          .eq(answerIdColumn, answer.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        return { ...answer, evaluation: evaluationData || null };
      }
      return answer;
    })) : [];

    const { data: aiAnswers } = await supabase.from(answerTable).select('*').eq('set_id', set_id).eq('is_ai_answer', true);
    const { data: sampleAnswers } = await supabase.from(answerTable).select('*').eq('set_id', set_id).eq('is_sample_answer', true);
    const { data: candidateAnswers } = await supabase.from(answerTable).select('*').eq('set_id', set_id).eq('is_candidate_answer', true);

    setSelected({
      set_id,
      exam_type,
      setInfo,
      myAnswers: processedMyAnswers,
      aiAnswers: aiAnswers || [],
      sampleAnswers: sampleAnswers || [],
      candidateAnswers: candidateAnswers || [],
    });
    setTab(0);
    setLoading(false);
  };

  const pagedSubmits = filteredSubmits.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Container maxWidth={false} sx={{ py: 6, px: { xs: 2, sm: 3, md: 4 }, maxWidth: '2000px', mx: 'auto' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>복습/비교</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <form onSubmit={e => { e.preventDefault(); setSearch(searchInput.trim()); }}>
          <TextField
            label="제목/과목 검색"
            variant="outlined"
            size="small"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            sx={{ width: 300 }}
          />
          <Button type="submit" variant="contained" sx={{ ml: 1 }}>검색</Button>
        </form>
      </Box>
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={60}>#</TableCell>
              <TableCell>시험명/과목</TableCell>
              <TableCell>제출일</TableCell>
              <TableCell>평가상태</TableCell>
              <TableCell>점수</TableCell>
              <TableCell>유형</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center"><CircularProgress /></TableCell>
              </TableRow>
            ) : pagedSubmits.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">제출한 답안이 없습니다.</TableCell>
              </TableRow>
            ) : (
              pagedSubmits.map((item, idx) => (
                <TableRow
                  key={item.exam_type + item.set_id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleSelect(item.set_id, item.exam_type)}
                  selected={selected && selected.set_id === item.set_id && selected.exam_type === item.exam_type}
                >
                  <TableCell>{filteredSubmits.length - ((page - 1) * PAGE_SIZE + idx)}</TableCell>
                  <TableCell>{item.exam_year}년 {item.exam_month} {item.set_title || item.subject}</TableCell>
                  <TableCell>{new Date(item.submission_time).toLocaleDateString()}</TableCell>
                  <TableCell>{item.evaluation_status || '-'}</TableCell>
                  <TableCell>{item.score ?? '-'}</TableCell>
                  <TableCell>{item.exam_type.toUpperCase()}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
        <Pagination
          count={Math.ceil(filteredSubmits.length / PAGE_SIZE)}
          page={page}
          onChange={(_, value) => setPage(value)}
          color="primary"
          siblingCount={1}
          boundaryCount={1}
          showFirstButton
          showLastButton
        />
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: '4fr 6fr', gap: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignSelf: 'flex-start', background: '#fafbfc', borderRadius: 3, border: '1px solid #e0e3e7', boxShadow: '0 2px 8px 0 rgba(60,60,60,0.07)', overflow: 'visible', p: 0 }}>
          {selected && selected.setInfo ? (
            selected.exam_type === 'mpt' ? (
              <>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, px: 2, pt: 2 }}>
                  <Tab label="File" />
                  <Tab label="Library" />
                </Tabs>
                {tab === 0 && selected.setInfo.file_url ? (
                  <PdfViewerTab url={selected.setInfo.file_url} label="문제 세트 PDF 1" />
                ) : null}
                {tab === 1 && selected.setInfo.library_url ? (
                  <PdfViewerTab url={selected.setInfo.library_url} label="문제 세트 PDF 2" />
                ) : null}
              </>
            ) : (
              selected.setInfo.q_url ? (
                <PdfViewerTab url={selected.setInfo.q_url} label="문제 PDF" />
              ) : <Typography color="text.secondary" sx={{ p: 3 }}>문제 PDF가 없습니다.</Typography>
            )
          ) : (
            <Typography color="text.secondary" sx={{ p: 3 }}>시험을 선택하세요.</Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: '#f5f7fa', borderRadius: 3, boxShadow: '0 2px 8px 0 rgba(60,60,60,0.07)', p: 2, border: '1px solid #e0e3e7', overflow: 'hidden', position: 'relative' }}>
          {loading ? (
             <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>
          ) : selected && selected.setInfo ? (
            (() => {
              const answerTabs = [
                {
                  label: '모범답안',
                  type: 'model',
                  url: selected.exam_type === 'mee'
                    ? selected.setInfo.a_url
                    : selected.setInfo.pointsheet_url,
                },
              ];
              if (selected.myAnswers && selected.myAnswers.length > 0) {
                selected.myAnswers.forEach((ans) => {
                  const date = ans.submission_time ? new Date(ans.submission_time) : null;
                  const dateStr = date ? `(${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')})` : '';
                  answerTabs.push({
                    label: `내 답안 ${dateStr}`,
                    type: 'mine',
                    answer: ans,
                    badge: ans.is_ai_answer ? 'AI 선정' : ans.is_sample_answer ? 'Sample 선정' : ans.is_candidate_answer ? 'Candidate 선정' : null,
                  });
                });
              }
              if (selected.aiAnswers && selected.aiAnswers.length > 0) {
                selected.aiAnswers.forEach((ans, idx) => {
                  answerTabs.push({ label: `AI 답안${selected.aiAnswers.length > 1 ? ` ${idx + 1}` : ''}`, type: 'ai', answer: ans });
                });
              }
              if (selected.sampleAnswers && selected.sampleAnswers.length > 0) {
                selected.sampleAnswers.forEach((ans, idx) => {
                  answerTabs.push({ label: `Sample 답안${selected.sampleAnswers.length > 1 ? ` ${idx + 1}` : ''}`, type: 'sample', answer: ans });
                });
              }
              if (selected.candidateAnswers && selected.candidateAnswers.length > 0) {
                selected.candidateAnswers.forEach((ans, idx) => {
                  answerTabs.push({ label: `Candidate 답안${selected.candidateAnswers.length > 1 ? ` ${idx + 1}` : ''}`, type: 'candidate', answer: ans });
                });
              }

              const currentTab = answerTabs[answerTab];

              return (
                <>
                  <Tabs value={answerTab} onChange={(_, v) => setAnswerTab(v)}
                    variant="scrollable" scrollButtons="auto"
                    sx={{ mb: 2, px: 1, minHeight: 36, borderBottom: 1, borderColor: 'divider', flexShrink: 0, overflowX: 'auto', '& .MuiTab-root': { minHeight: 36, py: 0.5, px: 2, mx: 0.5, borderRadius: 2, fontWeight: 600, background: '#f0f4fa', color: '#4B48DF', border: '1px solid #d0d7e2', transition: 'background 0.2s, color 0.2s', '&.Mui-selected': { background: '#4B48DF', color: '#fff', border: '1.5px solid #4B48DF', boxShadow: '0 2px 8px 0 rgba(60,60,60,0.07)', }, '&:hover': { background: '#e3e8f7', }, }, '& .MuiTabs-indicator': { display: 'none', }, }}>
                    {answerTabs.map((tab, index) => (
                      <Tab key={`${tab.type}-${index}`} label={tab.label} />
                    ))}
                  </Tabs>
                  <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
                    {currentTab?.type === 'model' && (
                      currentTab.url ? <PdfViewerTab url={currentTab.url} label="모범답안" /> : <Typography color="text.secondary" sx={{ p: 2 }}>모범답안 PDF가 없습니다.</Typography>
                    )}
                    {currentTab?.type === 'mine' && (
                      <Box>
                        {currentTab.answer.evaluation && (
                          <EvaluationReport evaluation={currentTab.answer.evaluation} />
                        )}
                        <Typography variant="subtitle1" gutterBottom>
                          내 답안
                          {currentTab.badge && (
                            <span style={{ background: '#ffeb3b', color: '#333', borderRadius: 4, padding: '2px 8px', marginLeft: 8, fontWeight: 600, fontSize: 14 }}>
                              {currentTab.badge}
                            </span>
                          )}
                        </Typography>
                        <Box sx={{ whiteSpace: 'pre-wrap', background: '#f8f9fa', p: 2, borderRadius: 2 }} dangerouslySetInnerHTML={{ __html: currentTab.answer?.answer_content || '' }} />
                      </Box>
                    )}
                    {currentTab?.type === 'ai' && (
                      <Box>
                        <Typography variant="subtitle1" gutterBottom>AI 답안</Typography>
                        <Box sx={{ whiteSpace: 'pre-wrap', background: '#f8f9fa', p: 2, borderRadius: 2 }} dangerouslySetInnerHTML={{ __html: currentTab.answer?.answer_content || '' }} />
                      </Box>
                    )}
                    {currentTab?.type === 'sample' && (
                      <Box>
                        <Typography variant="subtitle1" gutterBottom>Sample 답안</Typography>
                        <Box sx={{ whiteSpace: 'pre-wrap', background: '#f8f9fa', p: 2, borderRadius: 2 }} dangerouslySetInnerHTML={{ __html: currentTab.answer?.answer_content || '' }} />
                      </Box>
                    )}
                    {currentTab?.type === 'candidate' && (
                      <Box>
                        <Typography variant="subtitle1" gutterBottom>Candidate 답안</Typography>
                        <Box sx={{ whiteSpace: 'pre-wrap', background: '#f8f9fa', p: 2, borderRadius: 2 }} dangerouslySetInnerHTML={{ __html: currentTab.answer?.answer_content || '' }} />
                      </Box>
                    )}
                  </Box>
                </>
              );
            })()
          ) : (
            <Typography color="text.secondary" sx={{ p: 3 }}>시험을 선택하세요.</Typography>
          )}
        </Box>
      </Box>
    </Container>
  );
};

const PdfViewerTab = ({ url, label }) => {
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const containerRef = useRef(null);
  const [pdfWidth, setPdfWidth] = useState(800);

  useEffect(() => {
    const updatePdfSize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        setPdfWidth(Math.min(containerWidth - 16, 1200));
      }
    };
    updatePdfSize();
    window.addEventListener('resize', updatePdfSize);
    return () => window.removeEventListener('resize', updatePdfSize);
  }, []);

  useEffect(() => {
    setPage(1);
    setNumPages(null);
  }, [url]);

  return (
    <Box ref={containerRef} sx={{ width: '100%', height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
      {url ? (
        <>
          <Document file={url} onLoadSuccess={({ numPages }) => setNumPages(numPages)} onLoadError={console.error}>
            <Page pageNumber={page} width={pdfWidth} />
          </Document>
          <Box display="flex" alignItems="center" justifyContent="center" gap={2} mt={1} sx={{ mb: 2 }}>
            <Button size="small" variant="outlined" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>이전</Button>
            <Typography>{page} / {numPages || '?'}</Typography>
            <Button size="small" variant="outlined" onClick={() => setPage(p => Math.min(numPages || 1, p + 1))} disabled={!numPages || page >= numPages}>다음</Button>
          </Box>
        </>
      ) : (
        <Typography color="text.secondary">PDF가 없습니다.</Typography>
      )}
    </Box>
  );
};

export default MyReviewPage;
